import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { usePracas, useDefinirMinhasPracas } from "@/hooks/use-cotacoes";

/**
 * "Minhas praças" — onde este comprador compra.
 *
 * ⚠️ Componente compartilhado de propósito. Nasceu dentro da tela Praça, que está
 * DESLIGADA (`OFERTAS_ATIVAS`), e a mesma escolha é o filtro das Cotações dele:
 * deixá-lo lá teria produzido uma segunda cópia aqui, e as duas divergiriam na
 * primeira praça nova. A escolha é da ORGANIZAÇÃO, não de uma tela.
 *
 * `atuais` vem vazio quando a lista é DERIVADA (das praças dos produtores
 * vinculados): aquilo é palpite do servidor, e marcar as caixas com um palpite
 * faria o comprador salvar como escolha dele algo que ele não escolheu.
 */
export function DialogMinhasPracas({ atuais, onFechar }: { atuais: string[]; onFechar: () => void }) {
  const { toast } = useToast();
  const { data: pracas } = usePracas();
  const definir = useDefinirMinhasPracas();
  const [marcadas, setMarcadas] = useState<string[]>(atuais);

  function alternar(slug: string) {
    setMarcadas(prev => prev.indexOf(slug) >= 0 ? prev.filter(s => s !== slug) : prev.concat([slug]));
  }

  return (
    <Dialog open onOpenChange={aberto => { if (!aberto) onFechar(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Minhas praças</DialogTitle>
          <DialogDescription>Você vê as cargas ofertadas nas praças que marcar aqui.</DialogDescription>
        </DialogHeader>

        <ul className="space-y-2">
          {(pracas ?? []).map(p => (
            <li key={p.slug} className="flex items-center gap-3 rounded-lg border px-3 py-2.5">
              <Checkbox
                id={`praca-${p.slug}`}
                checked={marcadas.indexOf(p.slug) >= 0}
                onCheckedChange={() => alternar(p.slug)}
                data-testid={`check-praca-${p.slug}`}
              />
              <label htmlFor={`praca-${p.slug}`} className="text-sm font-medium cursor-pointer flex-1">
                {p.nome} <span className="text-xs text-muted-foreground">· {p.uf}</span>
              </label>
            </li>
          ))}
        </ul>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="min-h-11" onClick={onFechar}>Cancelar</Button>
          <Button
            className="min-h-11"
            disabled={definir.isPending}
            onClick={() => definir.mutate(marcadas, {
              onSuccess: () => { toast({ title: "Praças salvas" }); onFechar(); },
              onError: (e: any) => toast({ title: "Não foi possível salvar", description: e?.message, variant: "destructive" }),
            })}
            data-testid="button-salvar-pracas"
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
